import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetSecretValueResponse,
  ListSecretInfoResponse,
  Secret,
  SecretCreatedEnvelope,
  SecretDeletedEnvelope,
  SecretUpdatedEnvelope,
  createSecret as universalCreateSecret,
  deleteSecret as universalDeleteSecret,
  getSecretValue as universalGetSecretValue,
  listSecretInfo as universalListSecretInfo,
  updateSecret as universalUpdateSecret,
} from './velo-secrets-vault-v1-secret-secrets.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/secrets' };

export function getSecretValue(
  httpClient: HttpClient
): GetSecretValueSignature {
  return (name: string) =>
    universalGetSecretValue(
      name,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSecretValueSignature {
  /**
   * Retrieves a secret value by name.
   *
   * <blockquote class="caution">
   * __Caution:__
   * Only use a secret's value in the backend code. Returning the secret value in the frontend is a security risk.
   * </blockquote>
   * @param - The name of the secret to get the value of.
   */
  (name: string): Promise<NonNullablePaths<GetSecretValueResponse, `value`, 2>>;
}

export function listSecretInfo(
  httpClient: HttpClient
): ListSecretInfoSignature {
  return () =>
    universalListSecretInfo(
      // @ts-ignore
      { httpClient }
    );
}

interface ListSecretInfoSignature {
  /**
   * Retrieves a list of secrets.
   *
   * > **Note:** This method doesn't return the secret's value for security reasons. To retrieve the value, call [Get Secret Value](https://dev.wix.com/docs/rest/business-management/secrets/get-secret-value).
   */
  (): Promise<NonNullablePaths<ListSecretInfoResponse, `secrets`, 2>>;
}

export function createSecret(httpClient: HttpClient): CreateSecretSignature {
  return (secret: NonNullablePaths<Secret, `name` | `value`, 2>) =>
    universalCreateSecret(
      secret,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateSecretSignature {
  /**
   * Creates a secret.
   * @param - Fields of a new secret.
   * @returns Unique secret ID.
   */
  (secret: NonNullablePaths<Secret, `name` | `value`, 2>): Promise<string>;
}

export function deleteSecret(httpClient: HttpClient): DeleteSecretSignature {
  return (_id: string) =>
    universalDeleteSecret(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteSecretSignature {
  /**
   * Deletes a secret.
   *
   * <blockquote class="warning">
   * <strong>Warning:</strong>
   * Deleting a secret is irreversible and will break all code using the secret.
   * </blockquote>
   * @param - The unique ID of the secret to be deleted.
   */
  (_id: string): Promise<void>;
}

export function updateSecret(httpClient: HttpClient): UpdateSecretSignature {
  return (_id: string, secret: Secret) =>
    universalUpdateSecret(
      _id,
      secret,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateSecretSignature {
  /**
   * Updates 1 or all fields of a secret.
   *
   * To get the secret ID, call [List Secret Info](https://dev.wix.com/docs/rest/business-management/secrets/list-secret-info).
   * @param - ID of the secret to update.
   * @param - Details of a secret.
   */
  (_id: string, secret: Secret): Promise<void>;
}

export const onSecretCreated = EventDefinition(
  'wix.velo.secrets_vault.v1.secret_created',
  true,
  (event: SecretCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<SecretCreatedEnvelope>();
export const onSecretDeleted = EventDefinition(
  'wix.velo.secrets_vault.v1.secret_deleted',
  true,
  (event: SecretDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<SecretDeletedEnvelope>();
export const onSecretUpdated = EventDefinition(
  'wix.velo.secrets_vault.v1.secret_updated',
  true,
  (event: SecretUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<SecretUpdatedEnvelope>();

export {
  AccountInfo,
  ActionEvent,
  BaseEventMetadata,
  CreateSecretRequest,
  CreateSecretResponse,
  DeleteSecretRequest,
  DeleteSecretResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetSecretValueRequest,
  GetSecretValueResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListSecretInfoRequest,
  ListSecretInfoResponse,
  MessageEnvelope,
  RestoreInfo,
  Secret,
  SecretCreatedEnvelope,
  SecretDeletedEnvelope,
  SecretUpdatedEnvelope,
  UpdateSecretRequest,
  UpdateSecretResponse,
  WebhookIdentityType,
} from './velo-secrets-vault-v1-secret-secrets.universal.js';
